import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import TrainingMode from './TrainingMode'

// Staff portal - accessible at kittyscleaning.netlify.app/staff
// Staff enter their 4-digit PIN to see their routes and checklists

const DEFAULT_CHECKLISTS = {
  regular: ['Vacuuming all rooms','Mopping hard floors','Cleaning bathrooms (toilet, sink, shower/tub)','Wiping kitchen counters & appliances','Cleaning kitchen sink','Dusting surfaces & furniture','Emptying trash bins','Making beds / changing linens','Wiping mirrors & glass','Client walkthrough complete'],
  deep: ['Everything in Regular Clean','Inside oven cleaned','Inside refrigerator cleaned','Inside cabinets wiped down','Baseboards wiped','Window sills & tracks cleaned','Light fixtures & fans dusted','Walls spot-cleaned','Behind/under furniture cleaned','Detailed bathroom grout scrubbing','Client walkthrough complete'],
  airbnb: ['All linens changed & beds made hotel-style','Bathrooms fully sanitized','Kitchen cleaned & dishes put away','All trash removed','Floors vacuumed & mopped','Surfaces wiped & disinfected','Toiletries restocked (if provided)','Check for guest left items','Welcome setup complete (if applicable)','Photos taken of completed rooms','Client walkthrough complete'],
  moveout: ['All rooms vacuumed & mopped','Inside all cabinets & drawers cleaned','Inside oven & refrigerator cleaned','Bathrooms deep cleaned & sanitized','All walls spot-cleaned','Baseboards & trim wiped','Windows cleaned inside','All trash & debris removed','Light fixtures cleaned','Garage/patio swept (if applicable)','Final walkthrough with all rooms checked'],
  janitorial: ['Empty all trash bins & replace liners','Vacuum/sweep all floors','Mop hard floors','Clean & sanitize restrooms','Wipe down common surfaces & counters','Clean glass doors & entryways','Restock supplies (paper towels, soap, etc.)','Dust reachable surfaces','Final walkthrough of all areas'],
}

const TYPE_LABELS = {
  regular:'🧹 Regular', deep:'🫧 Deep Clean',
  airbnb:'🏠 Airbnb Turnover', moveout:'📦 Move-in/Move-out',
  janitorial:'🏢 Janitorial', custom:'✨ Custom',
}

function todayStr() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

function timeToMinutes(t) {
  if (!t) return Infinity
  const [hm, ap] = t.split(' ')
  let [h, m] = hm.split(':').map(Number)
  if (ap === 'PM' && h !== 12) h += 12
  if (ap === 'AM' && h === 12) h = 0
  return h * 60 + (m || 0)
}

function formatDuration(clockIn, clockOut) {
  const mins = Math.round((new Date(clockOut) - new Date(clockIn)) / 60000)
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins/60), m = mins%60
  return `${h}h ${m}min`
}


function WalkthroughStep({ seconds, onTick, onDone, exempt, client, job, checkItems, cleanTypeLabel,
  quickNoteText, setQuickNoteText, quickNoteSaved, onSaveNote, priorNote, checkedClientNotes, onToggleClientNote }) {
  useEffect(() => {
    if (exempt || seconds <= 0) return
    const t = setTimeout(() => onTick(s => s - 1), 1000)
    return () => clearTimeout(t)
  }, [seconds, exempt]) // eslint-disable-line

  return (
    <div>
      <div style={{ padding:'10px 12px', background:'#eaf2fb', borderRadius:10, marginBottom:10, border:'1px solid #c5d9f5' }}>
        <div style={{ fontSize:13, color:'#185fa5', lineHeight:1.5 }}>
          🏠 Como es la primera vez que vamos a esta casa (o aún no la hemos marcado como conocida), necesito que hagas un recorrido rápido antes de empezar.
        </div>
      </div>

      <div style={{ fontSize:11, fontWeight:800, color:'#e8547a', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>
        {cleanTypeLabel}
      </div>

      {client?.drive_link && (
        <a href={client.drive_link} target="_blank" rel="noreferrer"
          style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#e8f5e9', borderRadius:10, textDecoration:'none', marginBottom:10, border:'1px solid #a5d6a7' }}>
          <span style={{fontSize:18}}>📁</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'#1b5e20' }}>Fotos y referencia</div>
          </div>
          <span style={{ fontSize:12, color:'#2e7d32', fontWeight:500 }}>Abrir →</span>
        </a>
      )}

      {priorNote && (
        <div style={{ padding:'10px 12px', background:'#eef2ff', borderRadius:10, marginBottom:10, border:'1px solid #c7d2fe' }}>
          <div style={{ fontSize:9, fontWeight:700, color:'#3730a3', marginBottom:4 }}>📋 NOTA DEL EQUIPO ANTERIOR</div>
          <div style={{ fontSize:13, color:'#312e81' }}>{priorNote}</div>
        </div>
      )}

      {job.one_time_note && (
        <div style={{ padding:'10px 12px', background:'#fff8e1', borderRadius:10, marginBottom:10, border:'1px solid #ffe082' }}>
          <div style={{ fontSize:9, fontWeight:700, color:'#854f0b', marginBottom:4 }}>📝 NOTA DE ESTA VISITA</div>
          <div style={{ fontSize:13, color:'#5a4000' }}>{job.one_time_note}</div>
        </div>
      )}

      {client?.notes_list?.length > 0 ? (
        <div style={{ padding:'10px 12px', background:'#fdeef3', borderRadius:10, marginBottom:10, border:'1px solid #f4b8cc' }}>
          <div style={{ fontSize:9, fontWeight:700, color:'#c03060', marginBottom:6 }}>📋 NOTAS DEL CLIENTE (SIEMPRE) — tócalas para marcarlas</div>
          <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
            {client.notes_list.map((note,i) => {
              const done = checkedClientNotes.includes(note)
              return (
                <button key={i} onClick={()=>onToggleClientNote(note)}
                  style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 8px', borderRadius:8, border:'none', cursor:'pointer', textAlign:'left',
                    background: done ? '#f4b8cc' : 'transparent' }}>
                  <span style={{ fontSize:15, flexShrink:0 }}>{done?'☑':'☐'}</span>
                  <span style={{ fontSize:13, color:'#5a1a2a', textDecoration: done?'line-through':'none' }}>{note}</span>
                </button>
              )
            })}
          </div>
        </div>
      ) : client?.notes && (
        <div style={{ padding:'10px 12px', background:'#fdeef3', borderRadius:10, marginBottom:10, border:'1px solid #f4b8cc' }}>
          <div style={{ fontSize:9, fontWeight:700, color:'#c03060', marginBottom:4 }}>📋 NOTAS DEL CLIENTE (SIEMPRE)</div>
          <div style={{ fontSize:13, color:'#5a1a2a' }}>{client.notes}</div>
        </div>
      )}

      <div style={{ fontSize:11, fontWeight:700, color:'#80868b', marginBottom:6 }}>Checklist de esta casa:</div>
      <div style={{ display:'flex', flexDirection:'column', gap:5, marginBottom:14 }}>
        {checkItems.map(item => (
          <div key={item} style={{ padding:'8px 12px', borderRadius:9, background:'#f1f3f4', fontSize:13, color:'#5f6368' }}>• {item}</div>
        ))}
      </div>

      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:11, fontWeight:700, color:'#80868b', marginBottom:6 }}>¿El cliente te dijo algo justo ahora?</div>
        <textarea value={quickNoteText} onChange={e=>setQuickNoteText(e.target.value)}
          placeholder="Ej: pidió que no usemos cierto producto..."
          style={{ width:'100%', padding:10, border:'1px solid #e3e5e8', borderRadius:10, fontSize:13, resize:'none', height:56, fontFamily:'inherit' }} />
        <button onClick={onSaveNote} style={{ marginTop:6, padding:'8px 14px', borderRadius:9, border:'none', background:'#185fa5', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>
          {quickNoteSaved ? '✓ Guardado' : '💾 Guardar nota'}
        </button>
      </div>

      {!exempt && (
        <div style={{ textAlign:'center', marginBottom:14 }}>
          <div style={{ fontSize:12, color:'#a98a97', marginBottom:4 }}>🚶‍♀️ Haz un recorrido de la casa mientras lees esto</div>
          <div style={{ fontSize:32, fontWeight:800, color:'#e8547a' }}>{seconds}</div>
        </div>
      )}
      <button disabled={!exempt && seconds>0} onClick={onDone}
        style={{ width:'100%', padding:16, borderRadius:14, border:'none', fontWeight:800, fontSize:15,
          background: (!exempt && seconds>0) ? '#e3e5e8' : '#1d9e75', color: (!exempt && seconds>0) ? '#9aa0a6' : '#fff', cursor: (!exempt && seconds>0)?'default':'pointer' }}>
        {(!exempt && seconds>0) ? `Espera ${seconds}s...` : '✓ Ya hice el recorrido — Continuar'}
      </button>
    </div>
  )
}

function AssignWalkthroughStep({ team, myId, onChoose }) {
  return (
    <div>
      <div style={{ fontSize:16, fontWeight:800, color:'#3a2530', marginBottom:4, textAlign:'center' }}>¿Quién va a hacer el recorrido?</div>
      <div style={{ fontSize:12, color:'#a98a97', marginBottom:18, textAlign:'center' }}>Solo una persona necesita hacerlo — las demás pueden esperar.</div>
      <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:10 }}>
        {team.map(member => (
          <button key={member.id} onClick={()=>onChoose(member.id)}
            style={{ padding:14, borderRadius:12, border:'none', cursor:'pointer', fontSize:14, fontWeight:700,
              background: member.id===myId ? '#e8547a' : '#f1f3f4', color: member.id===myId ? '#fff' : '#5f6368' }}>
            {member.id===myId ? `Yo (${member.name})` : member.name}
          </button>
        ))}
        <button onClick={()=>onChoose('todos')}
          style={{ padding:14, borderRadius:12, border:'1px dashed #bdc1c6', cursor:'pointer', fontSize:13, fontWeight:600, background:'#fff', color:'#80868b' }}>
          Todos hacen su propio recorrido
        </button>
      </div>
    </div>
  )
}

function WaitingWalkthroughStep({ assigneeName, onCheck, checking }) {
  return (
    <div style={{ textAlign:'center', padding:'10px 0' }}>
      <div style={{ fontSize:44, marginBottom:10 }}>⏳</div>
      <div style={{ fontSize:17, fontWeight:800, color:'#3a2530', marginBottom:6 }}>Esperando a {assigneeName}</div>
      <div style={{ fontSize:13, color:'#a98a97', marginBottom:22 }}>En cuanto termine el recorrido, toca el botón para continuar.</div>
      <button onClick={onCheck} disabled={checking}
        style={{ width:'100%', padding:16, borderRadius:14, border:'none', background:'#185fa5', color:'#fff', fontWeight:800, fontSize:15, cursor:'pointer' }}>
        {checking ? 'Revisando...' : '🔄 Ya terminó, continuar'}
      </button>
    </div>
  )
}

function StaffAlertStep({ message, secs, onTick, onContinue }) {
  return (
    <div style={{ textAlign:'center', padding:'10px 0' }}>
      <div style={{ fontSize:44, marginBottom:10 }}>⚠️</div>
      <div style={{ fontSize:16, fontWeight:800, color:'#3a2530', marginBottom:14 }}>Aviso importante de Yosi</div>
      <div style={{ fontSize:14, color:'#5a4000', background:'#fff8e1', border:'1px solid #ffe082', borderRadius:12, padding:'14px 16px', marginBottom:20, lineHeight:1.6, textAlign:'left' }}>
        {message}
      </div>
      <GatedButton secs={secs} onTick={onTick} onClick={onContinue} label="Entendido, continuar →" color="#f59e0b" />
    </div>
  )
}

function GatedButton({ secs, onTick, onClick, label, waitingLabel, disabled, color='#1d9e75' }) {
  useEffect(() => {
    if (secs <= 0) return
    const t = setTimeout(() => onTick(s => s - 1), 1000)
    return () => clearTimeout(t)
  }, [secs]) // eslint-disable-line

  const waiting = secs > 0
  const blocked = waiting || disabled
  return (
    <button onClick={()=>!blocked && onClick()} disabled={blocked}
      style={{ width:'100%', padding:16, borderRadius:14, border:'none', fontWeight:800, fontSize:15,
        background: blocked ? '#e3e5e8' : color, color: blocked ? '#9aa0a6' : '#fff', cursor: blocked?'default':'pointer' }}>
      {waiting ? `Espera ${secs}s...` : (disabled ? (waitingLabel||label) : label)}
    </button>
  )
}

function TimeInfoStep({ hours, specialNote, newHouse, secs, onTick, onContinue }) {
  return (
    <div style={{ textAlign:'center', padding:'10px 0' }}>
      <div style={{ fontSize:44, marginBottom:10 }}>⏱️</div>
      <div style={{ fontSize:17, fontWeight:800, color:'#3a2530', marginBottom:10 }}>Este trabajo está calculado para {hours}h</div>
      <div style={{ fontSize:12, color:'#5f6368', marginBottom:12, lineHeight:1.5, padding:'0 6px' }}>
        {newHouse ? 'Ya confirmaste el tiempo para esta casa.' : 'Ya hemos ido antes, así que el tiempo ya está calculado. No es necesario estimarlo.'}
      </div>
      {specialNote && (
        <div style={{ fontSize:12, fontWeight:600, color:'#c05050', background:'#fff3f3', border:'1px solid #f4b8b8', borderRadius:10, padding:'10px 12px', marginBottom:16 }}>
          ⚠️ {specialNote}
        </div>
      )}
      <div style={{ fontSize:12, fontWeight:600, color:'#854f0b', background:'#fff8e1', border:'1px solid #ffe082', borderRadius:10, padding:'10px 12px', marginBottom:16, textAlign:'left' }}>
        📞 Si hay exceso de suciedad o el cliente pidió algo extra, llama a Yosi directamente para avisarle.
      </div>

      <GatedButton secs={secs} onTick={onTick} onClick={onContinue} label="Entendido, continuar →" />
    </div>
  )
}

function EstimateStep({ hours, setHours, scheduledHours, note, setNote, submitting, secs, onTick, onSubmit }) {
  const [mode, setMode] = useState(null) // null | 'more' | 'less'
  function pick(m) {
    setMode(m)
    setHours(m === 'more' ? scheduledHours + 0.5 : Math.max(0.5, scheduledHours - 0.5))
  }
  const diff = hours - scheduledHours
  const validChange = mode === 'more' ? diff > 0 : mode === 'less' ? diff < 0 : false
  return (
    <div style={{ textAlign:'center' }}>
      <div style={{ fontSize:11, fontWeight:800, color:'#185fa5', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>🏠 Primera vez en esta casa</div>
      <div style={{ fontSize:13, color:'#5f6368', marginBottom:4 }}>Yosi programó</div>
      <div style={{ fontSize:40, fontWeight:800, color:'#e8547a', lineHeight:1.1, marginBottom:8 }}>{scheduledHours}h</div>
      <div style={{ fontSize:13, color:'#3a2530', marginBottom:18, lineHeight:1.5, padding:'0 6px' }}>
        Este es el tiempo para esta casa. Después de ver la casa, confírmalo.<br/>
        <span style={{ color:'#80868b', fontSize:12 }}>Solo si de verdad se necesita más o menos tiempo, avísale aquí para que Yosi lo confirme con el cliente.</span>
      </div>

      {mode === null ? (
        <>
          <GatedButton secs={secs} onTick={onTick} onClick={()=>onSubmit('ok')} disabled={submitting} label={`✓ Sí, ${scheduledHours}h está bien`} color="#1d9e75" />
          <div style={{ display:'flex', gap:8, marginTop:12 }}>
            <button onClick={()=>pick('less')} style={{ flex:1, padding:'11px 8px', borderRadius:10, border:'1.5px solid #e3e5e8', background:'#fff', color:'#5f6368', fontSize:13, fontWeight:600, cursor:'pointer' }}>Necesito menos</button>
            <button onClick={()=>pick('more')} style={{ flex:1, padding:'11px 8px', borderRadius:10, border:'1.5px solid #e3e5e8', background:'#fff', color:'#5f6368', fontSize:13, fontWeight:600, cursor:'pointer' }}>Necesito más</button>
          </div>
        </>
      ) : (
        <div style={{ background:'#f8f9fa', borderRadius:12, padding:'14px 12px', textAlign:'center' }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#3a2530', marginBottom:10 }}>
            {mode === 'more' ? '¿Cuánto tiempo crees que se necesita?' : '¿Cuánto tiempo crees que es suficiente?'}
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:20, marginBottom:12 }}>
            <button onClick={()=>setHours(h=>Math.max(0.5,h-0.5))} style={{ width:44, height:44, borderRadius:'50%', border:'none', background:'#e3e5e8', fontSize:22, fontWeight:800, color:'#5f6368', cursor:'pointer' }}>−</button>
            <div style={{ fontSize:32, fontWeight:800, color:'#e8547a', minWidth:80 }}>{hours}h</div>
            <button onClick={()=>setHours(h=>h+0.5)} style={{ width:44, height:44, borderRadius:'50%', border:'none', background:'#e3e5e8', fontSize:22, fontWeight:800, color:'#5f6368', cursor:'pointer' }}>+</button>
          </div>
          <textarea value={note} onChange={e=>setNote(e.target.value)} placeholder="¿Por qué? (ej: mucha suciedad, casa más grande, el cliente pidió algo extra)"
            style={{ width:'100%', minHeight:60, padding:'8px 10px', borderRadius:8, border:'1px solid #e3e5e8', fontSize:13, fontFamily:'inherit', marginBottom:10, boxSizing:'border-box' }} />
          <button onClick={()=>onSubmit(mode)} disabled={submitting || !validChange}
            style={{ width:'100%', padding:'12px', borderRadius:10, border:'none', background: validChange ? '#e8547a' : '#e3e5e8', color: validChange ? '#fff' : '#9aa0a6', fontSize:14, fontWeight:700, cursor: validChange ? 'pointer' : 'not-allowed' }}>
            {submitting ? 'Enviando...' : `Avisar a Yosi: ${hours}h`}
          </button>
          <button onClick={()=>setMode(null)} style={{ marginTop:8, background:'none', border:'none', color:'#80868b', fontSize:12, cursor:'pointer' }}>← Volver, {scheduledHours}h está bien</button>
        </div>
      )}
    </div>
  )
}

function ChecklistFinalStep({ checkItems, checked, onToggle, allDone, comments, onCommentChange, onSignOff, cleanType, onGoBack,
  showDamageForm, setShowDamageForm, damagePhoto, setDamagePhoto, damageExplanation, setDamageExplanation, onSubmitDamage, damageSaved, submittingDamage,
  clientNotesList, checkedClientNotes, onToggleClientNote, missingSupplies, setMissingSupplies }) {
  const allNotesChecked = !clientNotesList?.length || clientNotesList.every(n=>checkedClientNotes.includes(n))
  const canSign = allDone && allNotesChecked
  return (
    <div>
      {onGoBack && (
        <button onClick={onGoBack} style={{ width:'100%', border:'1.5px solid #185fa5', background:'#eaf2fb', color:'#185fa5', fontSize:13, fontWeight:700, cursor:'pointer', padding:'10px 14px', borderRadius:10, marginBottom:14 }}>
          ← Atrás — ver casa e info otra vez
        </button>
      )}
      {clientNotesList?.length > 0 && (
        <div style={{ padding:'10px 12px', background:'#fdeef3', borderRadius:10, marginBottom:14, border:'1px solid #f4b8cc' }}>
          <div style={{ fontSize:9, fontWeight:700, color:'#c03060', marginBottom:6 }}>📋 NOTAS DEL CLIENTE — marca todas antes de firmar</div>
          <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
            {clientNotesList.map((note,i) => {
              const done = checkedClientNotes.includes(note)
              return (
                <button key={i} onClick={()=>onToggleClientNote(note)}
                  style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 8px', borderRadius:8, border:'none', cursor:'pointer', textAlign:'left',
                    background: done ? '#f4b8cc' : 'transparent' }}>
                  <span style={{ fontSize:15, flexShrink:0 }}>{done?'☑':'☐'}</span>
                  <span style={{ fontSize:13, color:'#5a1a2a', textDecoration: done?'line-through':'none' }}>{note}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div style={{ fontSize:11, fontWeight:800, color:'#e8547a', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:10 }}>
        ✅ Marca cada tarea — {checked.length}/{checkItems.length}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:5, marginBottom:14 }}>
        {checkItems.map(item => {
          const done = checked.includes(item)
          return (
            <button key={item} onClick={()=>onToggle(item)}
              style={{ padding:'11px 14px', borderRadius:10, border:'none', cursor:'pointer',
                background: done ? '#e8547a' : '#f1f3f4', color: done ? '#fff' : '#5f6368',
                fontSize:13, fontWeight: done?600:400, textAlign:'left', display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:16 }}>{done?'✓':'○'}</span>{item}
            </button>
          )
        })}
      </div>

      {cleanType === 'airbnb' && (
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#80868b', marginBottom:4 }}>📦 ¿Faltó algún relleno/suministro? (para poder ordenarlo)</div>
          <textarea value={missingSupplies} onChange={e=>setMissingSupplies(e.target.value)}
            placeholder="Ej: papel de baño, toallas de papel, jabón..."
            style={{ width:'100%', padding:10, border:'1px solid #e3e5e8', borderRadius:10, fontSize:13, resize:'none', height:50, fontFamily:'inherit' }} />
        </div>
      )}

      {!showDamageForm ? (
        <button onClick={()=>setShowDamageForm(true)}
          style={{ width:'100%', padding:'10px', borderRadius:10, border:'1px dashed #f4b8b8', background:'#fff8f8', color:'#c05050', fontSize:12, fontWeight:600, cursor:'pointer', marginBottom:14 }}>
          📷 ¿Algo ya estaba dañado o roto? Repórtalo aquí
        </button>
      ) : (
        <div style={{ padding:'12px', background:'#fff8f8', borderRadius:10, border:'1px solid #f4b8b8', marginBottom:14 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#c05050', marginBottom:8 }}>📷 Reportar algo ya dañado</div>
          <label style={{ display:'block', padding:'10px', border:'1px dashed #f4b8b8', borderRadius:8, textAlign:'center', fontSize:12, color:'#80868b', marginBottom:8, cursor:'pointer' }}>
            {damagePhoto ? `📷 ${damagePhoto.name}` : '📷 Tomar o subir foto'}
            <input type="file" accept="image/*" capture="environment" style={{ display:'none' }}
              onChange={e=>setDamagePhoto(e.target.files?.[0]||null)} />
          </label>
          <textarea value={damageExplanation} onChange={e=>setDamageExplanation(e.target.value)}
            placeholder="Explica qué encontraste..."
            style={{ width:'100%', padding:10, border:'1px solid #e3e5e8', borderRadius:8, fontSize:13, resize:'none', height:56, fontFamily:'inherit', marginBottom:8 }} />
          <button onClick={onSubmitDamage} disabled={submittingDamage || !damageExplanation.trim()}
            style={{ width:'100%', padding:'9px', borderRadius:8, border:'none', background: damageSaved ? '#1d9e75' : '#c05050', color:'#fff', fontWeight:700, fontSize:12, cursor:'pointer' }}>
            {damageSaved ? '✓ Reportado' : submittingDamage ? 'Enviando...' : 'Enviar reporte'}
          </button>
        </div>
      )}

      <div style={{ fontSize:11, fontWeight:700, color:'#80868b', marginBottom:4 }}>Para el próximo equipo que limpie esta casa:</div>
      <textarea value={comments} onChange={e=>onCommentChange(e.target.value)}
        placeholder="¿Algo que no se pudo terminar, o que la próxima persona debería saber?"
        style={{ width:'100%', padding:10, border:'1px solid #e3e5e8', borderRadius:10, fontSize:13, resize:'none', height:60, fontFamily:'inherit', marginBottom:14 }} />
      <button onClick={()=>canSign&&onSignOff()} disabled={!canSign}
        style={{ width:'100%', padding:16, borderRadius:14, border:'none', fontWeight:800, fontSize:15,
          background: canSign ? '#1d9e75' : '#f0f0f0', color: canSign ? '#fff' : '#9aa0a6', cursor: canSign?'pointer':'default' }}>
        {!allNotesChecked ? 'Marca las notas del cliente' : !allDone ? 'Completa todo para firmar' : '✍️ Firmar'}
      </button>
    </div>
  )
}

function EtaStep({ nextClient, nextTime, nextAddress, etaText, setEtaText, etaChoice, setEtaChoice, onSubmit }) {
  return (
    <div>
      <div style={{ fontSize:11, fontWeight:800, color:'#e8547a', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6, textAlign:'center' }}>
        🏠 Próxima casa
      </div>
      <div style={{ fontSize:20, fontWeight:800, color:'#3a2530', textAlign:'center', marginBottom:2 }}>{nextClient}</div>
      {nextAddress && (
        <div style={{ fontSize:12, color:'#80868b', textAlign:'center', marginBottom:2 }}>📍 {nextAddress}</div>
      )}
      <div style={{ fontSize:13, color:'#a98a97', textAlign:'center', marginBottom:16 }}>Te esperan a las {nextTime}</div>
      <div style={{ fontSize:12, color:'#5f6368', textAlign:'center', marginBottom:16, lineHeight:1.5, padding:'0 6px' }}>
        Si vas a llegar antes de esa hora, dinos qué prefieres — así le preguntamos al cliente si le parece bien que llegues más temprano.
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:12 }}>
        <button onClick={()=>setEtaChoice('ontime')}
          style={{ padding:14, borderRadius:12, border:'none', cursor:'pointer', fontSize:13, fontWeight:700,
            background: etaChoice==='ontime' ? '#e8547a' : '#f1f3f4', color: etaChoice==='ontime' ? '#fff' : '#5f6368' }}>
          🕐 Llegaré a tiempo
        </button>
        <button onClick={()=>setEtaChoice('break')}
          style={{ padding:14, borderRadius:12, border:'none', cursor:'pointer', fontSize:13, fontWeight:700,
            background: etaChoice==='break' ? '#e8547a' : '#f1f3f4', color: etaChoice==='break' ? '#fff' : '#5f6368' }}>
          ☕ Voy antes, prefiero tomar un descanso
        </button>
        <button onClick={()=>setEtaChoice('early')}
          style={{ padding:14, borderRadius:12, border:'none', cursor:'pointer', fontSize:13, fontWeight:700,
            background: etaChoice==='early' ? '#e8547a' : '#f1f3f4', color: etaChoice==='early' ? '#fff' : '#5f6368' }}>
          🏃 Voy antes, lista para empezar
        </button>
      </div>
      <div style={{ fontSize:11, fontWeight:700, color:'#80868b', marginBottom:6, textAlign:'center' }}>¿A qué hora crees que llegas?</div>
      <input type="time" value={etaText} onChange={e=>setEtaText(e.target.value)}
        style={{ width:'100%', padding:12, border:'1px solid #e3e5e8', borderRadius:10, fontSize:15, marginBottom:14, textAlign:'center' }} />
      <button onClick={onSubmit} disabled={!etaChoice || !etaText.trim()}
        style={{ width:'100%', padding:16, borderRadius:14, border:'none', fontWeight:800, fontSize:15,
          background: (etaChoice && etaText.trim()) ? '#1d9e75' : '#f0f0f0', color: (etaChoice && etaText.trim()) ? '#fff' : '#9aa0a6', cursor: (etaChoice && etaText.trim())?'pointer':'default' }}>
        {!etaChoice ? 'Elige una opción' : !etaText.trim() ? 'Escribe tu hora estimada' : 'Enviar →'}
      </button>
    </div>
  )
}

function WelcomeScreen({ staffName, teammateNames, houseCount, totalHours, onStart }) {
  return (
    <div style={{ background:'linear-gradient(180deg,#fff 0%,#fff8fb 100%)', borderRadius:20, padding:'28px 22px', border:'1px solid #fde3ec', textAlign:'center' }}>
      <div style={{ fontSize:40, marginBottom:10 }}>🐈☀️</div>
      <div style={{ fontSize:19, fontWeight:800, color:'#3a2530', marginBottom:4 }}>¡Buenos días, {staffName}!</div>
      {teammateNames.length > 0 && (
        <div style={{ fontSize:14, color:'#5f6368', marginBottom:14 }}>Hoy trabajas con {teammateNames.join(' y ')}</div>
      )}
      <div style={{ display:'flex', justifyContent:'center', gap:24, marginBottom:20 }}>
        <div>
          <div style={{ fontSize:26, fontWeight:800, color:'#e8547a' }}>{houseCount}</div>
          <div style={{ fontSize:11, color:'#80868b' }}>casas hoy</div>
        </div>
        <div>
          <div style={{ fontSize:26, fontWeight:800, color:'#e8547a' }}>{totalHours}h</div>
          <div style={{ fontSize:11, color:'#80868b' }}>estimadas</div>
        </div>
      </div>
      <div style={{ fontSize:13, color:'#a98a97', marginBottom:20 }}>¡Que tengas un buen día! 🐾</div>
      <button onClick={onStart}
        style={{ width:'100%', padding:16, borderRadius:14, border:'none', background:'#1d9e75', color:'#fff', fontWeight:800, fontSize:15, cursor:'pointer' }}>
        Empezar mi día →
      </button>
    </div>
  )
}

function PhotoConfirmStep({ type, cleanType, onConfirm, confirming, secs, onTick, onGoBack }) {
  const isBefore = type === 'before'
  const isAirbnb = cleanType === 'airbnb'
  return (
    <div style={{ textAlign:'center', padding:'10px 0' }}>
      {isBefore && onGoBack && (
        <button onClick={onGoBack} style={{ width:'100%', border:'1.5px solid #185fa5', background:'#eaf2fb', color:'#185fa5', fontSize:13, fontWeight:700, cursor:'pointer', padding:'10px 14px', borderRadius:10, marginBottom:14 }}>
          ← Atrás — ver casa e info otra vez
        </button>
      )}
      <div style={{ fontSize:44, marginBottom:10 }}>📸</div>
      <div style={{ fontSize:17, fontWeight:800, color:'#3a2530', marginBottom:6 }}>
        {isBefore ? '¿Ya enviaste las fotos de ANTES?' : '¿Ya enviaste las fotos de DESPUÉS?'}
      </div>
      <div style={{ fontSize:13, color:'#a98a97', marginBottom:isBefore?12:22 }}>
        Envíalas por WhatsApp a Yosi {isBefore ? 'antes de empezar a limpiar' : 'ahora que ya terminaste'}.
      </div>
      {isBefore && (
        <div style={{ fontSize:12, fontWeight:600, color:'#c05050', background:'#fff3f3', border:'1px solid #f4b8b8', borderRadius:10, padding:'8px 12px', marginBottom:22 }}>
          {isAirbnb
            ? '📌 Recuerda: fotos de TODOS los cuartos — especialmente el relleno/suministros, las toallas, y los botes de basura (abiertos, para mostrar que están vacíos).'
            : '📌 Recuerda: solo tomamos fotos de la cocina y los baños.'}
        </div>
      )}
      <GatedButton secs={secs} onTick={onTick} onClick={onConfirm} disabled={confirming} label="✓ Sí, ya las envié" />
    </div>
  )
}

export default function StaffPortal() {
  const [pin, setPin] = useState('')
  const [showTraining, setShowTraining] = useState(false)
  const [showFullSchedule, setShowFullSchedule] = useState(false)
  const [staffMember, setStaffMember] = useState(null)
  const [error, setError] = useState('')
  const [jobs, setJobs] = useState([])
  const [clients, setClients] = useState([])
  const [templates, setTemplates] = useState(DEFAULT_CHECKLISTS)
  const [checklist, setChecklist] = useState({})
  const [signoffs, setSignoffs] = useState({})
  const [comments, setComments] = useState({})
  const [showSign, setShowSign] = useState(null)
  const [initials, setInitials] = useState('')
  const [loading, setLoading] = useState(false)
  const selectedDate = todayStr()
  const [saving, setSaving] = useState({})
  const [announcements, setAnnouncements] = useState([])
  const [myReactions, setMyReactions] = useState({}) // announcementId -> emoji
  const [equipmentItems, setEquipmentItems] = useState([])
  const [equipmentCheckedOut, setEquipmentCheckedOut] = useState(true) // true until we know otherwise, avoids flashing the gate
  const [selectedEquipment, setSelectedEquipment] = useState([]) // array of equipment_item ids
  const [equipmentIssue, setEquipmentIssue] = useState('')
  const [submittingEquipment, setSubmittingEquipment] = useState(false)
  const [timeLogs, setTimeLogs] = useState({}) // {jobId: {clock_in, clock_out}}
  const [justClockedOut, setJustClockedOut] = useState(null) // jobId briefly, for celebration
  const [confirmAction, setConfirmAction] = useState(null) // {type:'in'|'out', jobId, clientName}
  const [flowStep, setFlowStep] = useState(null) // null|'walkthrough'|'assign_walkthrough'|'waiting_walkthrough'|'estimate'|'time_info'|'checklist_final'|'eta'
  const [walkthroughSecs, setWalkthroughSecs] = useState(5)
  const [gateSecs, setGateSecs] = useState(5) // shared 5s countdown for every step after clock-in
  const [jobWalkthroughs, setJobWalkthroughs] = useState({}) // jobId -> row (team-wide, whoever did it)
  const [jobEstimates, setJobEstimates] = useState({})       // jobId -> MY row (each person answers separately)
  const [beenBefore, setBeenBefore] = useState({})           // clientId -> true if we've cleaned there before today
  const [estimateNote, setEstimateNote] = useState('')
  const [estimateInput, setEstimateInput] = useState(2)
  const [submittingEstimate, setSubmittingEstimate] = useState(false)
  const [quickNoteText, setQuickNoteText] = useState('')
  const [quickNoteSaved, setQuickNoteSaved] = useState(false)
  const [etaText, setEtaText] = useState('')
  const [etaChoice, setEtaChoice] = useState(null)
  const [teamStaffIds, setTeamStaffIds] = useState([])
  const [allStaff, setAllStaff] = useState([])
  const [showWelcome, setShowWelcome] = useState(true)
  const [walkthroughAssignee, setWalkthroughAssignee] = useState(null) // staffId of whoever's doing it, or 'self'/'todos'
  const [priorHandoffNotes, setPriorHandoffNotes] = useState({}) // jobId -> last visit's note for this client
  const [checkingApproval, setCheckingApproval] = useState(false)
  const [showDamageForm, setShowDamageForm] = useState(false)
  const [damagePhoto, setDamagePhoto] = useState(null)
  const [damageExplanation, setDamageExplanation] = useState('')
  const [submittingDamage, setSubmittingDamage] = useState(false)
  const [damageSaved, setDamageSaved] = useState(false)
  const [missingSupplies, setMissingSupplies] = useState('')
  const [photoConfirmations, setPhotoConfirmations] = useState({}) // jobId -> {before_sent, after_sent}
  const [confirmingPhotos, setConfirmingPhotos] = useState(false)

  // Check if already logged in
  useEffect(() => {
    const saved = localStorage.getItem('staff_portal_id')
    if (saved) loadStaff(saved)
  }, [])

  useEffect(() => {
    if (staffMember) fetchJobs()
  }, [staffMember, selectedDate])

  async function login() {
    if (pin.length !== 6) { setError('Ingresa tu PIN de 6 dígitos'); return }
    setLoading(true)
    const { data } = await supabase.from('staff').select('*').eq('pin', pin).eq('is_active', true).maybeSingle()
    if (!data) { setError('PIN incorrecto'); setLoading(false); return }
    localStorage.setItem('staff_portal_id', data.id)
    setStaffMember(data)
    setInitials(data.name.split(' ').map(w=>w[0]).join('').toUpperCase())
    setLoading(false)
  }

  async function loadStaff(id) {
    const { data } = await supabase.from('staff').select('*').eq('id', id).maybeSingle()
    if (data) {
      setStaffMember(data)
      setInitials(data.name.split(' ').map(w=>w[0]).join('').toUpperCase())
    }
  }

  async function fetchJobs() {
    setLoading(true)
    // Get routes this staff member is assigned to today
const [{ data: routes }, { data: cl }, { data: tmpl }, { data: so }, { data: ann }, { data: allSt }] = await Promise.all([
      supabase.from('routes').select('*').eq('route_date', selectedDate),
      supabase.from('clients').select('*'),
      supabase.from('checklist_templates').select('*'),
      supabase.from('job_signoffs').select('*').eq('scheduled_date', selectedDate).eq('staff_name', staffMember.name),
      supabase.from('staff_announcements').select('*').order('created_at', { ascending: false }).limit(10),
      supabase.from('staff').select('*').eq('is_active', true),
    ])
    setAllStaff(allSt||[])
    const myAnn = (ann||[]).filter(a => {
      const targets = a.target_staff_ids?.length ? a.target_staff_ids : (a.target_staff_id ? [a.target_staff_id] : [])
      return targets.length === 0 || targets.includes(staffMember.id)
    })
    setAnnouncements(myAnn)
    if (myAnn.length) {
      const { data: rx } = await supabase.from('announcement_reactions').select('*')
        .eq('staff_id', staffMember.id).in('announcement_id', myAnn.map(a=>a.id))
      const rxMap = {}
      ;(rx||[]).forEach(r => { rxMap[r.announcement_id] = r.emoji })
      setMyReactions(rxMap)
    }

    const { data: eq } = await supabase.from('equipment_items').select('*').eq('is_active', true).order('name')
    setEquipmentItems(eq||[])
    const { data: co } = await supabase.from('equipment_checkouts').select('*').eq('staff_id', staffMember.id).eq('date', selectedDate)
    setEquipmentCheckedOut((co||[]).length > 0 || (eq||[]).length === 0)

    // Find routes this staff member is on
    const myRoutes = (routes||[]).filter(r => {
      try { return (JSON.parse(r.notes||'{}').staffIds||[]).includes(staffMember.id) } catch(e) { return false }
    })
    const myRouteLabels = myRoutes.map(r=>r.label)

    // Everyone else on the same route(s) today, for walkthrough delegation
    const teamSet = new Set()
    myRoutes.forEach(r => {
      try { (JSON.parse(r.notes||'{}').staffIds||[]).forEach(id=>teamSet.add(id)) } catch(e) {}
    })
    setTeamStaffIds(Array.from(teamSet))

    // Get jobs on those routes
    const { data: jb } = await supabase.from('jobs').select('*')
      .eq('scheduled_date', selectedDate)
      .in('route', myRouteLabels.length ? myRouteLabels : ['__none__'])

    const sortedJb = [...(jb||[])].sort((a,b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time))
    setJobs(sortedJb)
    setClients(cl||[])

    // Load today's clock in/out times, walkthroughs, estimates, and photo confirmations
    if (jb?.length) {
      const jobIds = jb.map(j=>j.id)
      const [{ data: tl }, { data: wt }, { data: est }, { data: pc }] = await Promise.all([
        supabase.from('time_logs').select('*').eq('staff_id', staffMember.id).in('job_id', jobIds),
        supabase.from('job_walkthroughs').select('*').in('job_id', jobIds),
        supabase.from('job_estimates').select('*').in('job_id', jobIds),
        supabase.from('photo_confirmations').select('*').in('job_id', jobIds),
      ])
      const tlMap = {}
      ;(tl||[]).forEach(t => { tlMap[t.job_id] = t })
      setTimeLogs(tlMap)
      const wtMap = {}
      ;(wt||[]).forEach(w => { wtMap[w.job_id] = w })
      setJobWalkthroughs(wtMap)
      const estMap = {}
      ;(est||[]).filter(e => e.staff_id === staffMember.id).forEach(e => { estMap[e.job_id] = e })
      setJobEstimates(estMap)

      // A house is "new" only if we have never cleaned there before today.
      // Past jobs (not skipped) or cleaning history both count as having been there.
      const clientIds = [...new Set(jb.map(j=>j.client_id).filter(Boolean))]
      const before = {}
      if (clientIds.length) {
        const [{ data: pastJobs }, hist] = await Promise.all([
          supabase.from('jobs').select('client_id').in('client_id', clientIds)
            .lt('scheduled_date', selectedDate).in('status', ['scheduled','completed']).limit(1000),
          supabase.from('cleaning_history').select('client_id').in('client_id', clientIds)
            .lt('cleaned_date', selectedDate).limit(1000),
        ])
        ;(pastJobs||[]).forEach(r => { before[r.client_id] = true })
        ;(hist?.data||[]).forEach(r => { before[r.client_id] = true })
      }
      setBeenBefore(before)
      const pcMap = {}
      ;(pc||[]).forEach(p => { pcMap[p.job_id] = p })
      setPhotoConfirmations(pcMap)

      // Restore where they were mid-job if the page got refreshed — otherwise a
      // refresh would drop flowStep back to null and jump straight to the
      // interactive checklist, skipping walkthrough/estimate/etc. entirely.
      const RESTORABLE_STEPS = ['staff_alert','assign_walkthrough','waiting_walkthrough','walkthrough','estimate','time_info','before_photos_confirm','checklist_final']
      const currentJobNow = sortedJb.find(j => !tlMap[j.id]?.clock_out)
      if (currentJobNow) {
        const { data: dayStatus } = await supabase.from('staff_day_status').select('*')
          .eq('staff_id', staffMember.id).eq('date', selectedDate).maybeSingle()
        if (dayStatus && dayStatus.job_id === currentJobNow.id && RESTORABLE_STEPS.includes(dayStatus.step)) {
          setGateSecs(5)
          setFlowStep(dayStatus.step)
        }
      }
    } else {
      setTimeLogs({})
      setJobWalkthroughs({})
      setJobEstimates({})
      setPhotoConfirmations({})
    }

    if (tmpl?.length) {
      const map = {}
      tmpl.forEach(t => { map[t.clean_type] = t.items })
      setTemplates({ ...DEFAULT_CHECKLISTS, ...map })
    }

    // Load existing signoffs
    const soMap = {}
    ;(so||[]).forEach(s => { soMap[s.job_id] = s })
    setSignoffs(soMap)

    // Pre-fill checklist from saved signoffs
    const clMap = {}
    ;(so||[]).forEach(s => { clMap[s.job_id] = s.checked_items || [] })
    setChecklist(clMap)

    const cmMap = {}
    ;(so||[]).forEach(s => { if(s.comments) cmMap[s.job_id] = s.comments })
    setComments(cmMap)

    setLoading(false)
  }

  function toggleCheck(jobId, item) {
    if (signoffs[jobId]) return // already signed
    setChecklist(prev => {
      const current = prev[jobId] || []
      return { ...prev, [jobId]: current.includes(item) ? current.filter(x=>x!==item) : [...current,item] }
    })
  }

  async function signOff(jobId) {
    if (!initials.trim()) return
    const job = jobs.find(j=>j.id===jobId)
    const client = clients.find(c=>c.id===job?.client_id)
    const cleanType = job?.clean_type || 'regular'
    const checked = checklist[jobId] || []
    const jobComments = comments[jobId] || ''

    setSaving(p=>({...p,[jobId]:true}))
    const { data, error } = await supabase.from('job_signoffs').upsert({
      job_id: jobId,
      client_id: job?.client_id,
      route_label: job?.route,
      scheduled_date: selectedDate,
      clean_type: cleanType,
      checked_items: checked,
      initials: initials.toUpperCase(),
      staff_name: staffMember.name,
      comments: jobComments,
      missing_supplies: cleanType==='airbnb' ? (missingSupplies.trim()||null) : null,
      signed_at: new Date().toISOString()
    }, { onConflict: 'job_id,staff_name' })

    if (!error) {
      setSignoffs(p=>({...p,[jobId]:{ initials, staff_name: staffMember.name, checked_items: checked, comments: jobComments, signed_at: new Date().toISOString() }}))
      setGateSecs(5)
      setMissingSupplies('')
    }
    setSaving(p=>({...p,[jobId]:false}))
    setShowSign(null)
    setFlowStep(null)
  }

  async function reactToAnnouncement(id, emoji) {
    await supabase.from('announcement_reactions').upsert({
      announcement_id: id, staff_id: staffMember.id, emoji
    }, { onConflict: 'announcement_id,staff_id' })
    setMyReactions(p => ({ ...p, [id]: emoji }))
  }

  async function submitEquipmentCheckout() {
    if (selectedEquipment.length === 0) return
    setSubmittingEquipment(true)
    await supabase.from('equipment_checkouts').upsert(
      selectedEquipment.map(itemId => ({
        staff_id: staffMember.id, date: selectedDate, equipment_item_id: itemId,
        issue_note: equipmentIssue.trim() || null
      })),
      { onConflict: 'staff_id,date,equipment_item_id' }
    )
    setSubmittingEquipment(false)
    setEquipmentCheckedOut(true)
  }

  function toggleEquipment(itemId) {
    setSelectedEquipment(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId])
  }

  async function updateDayStatus(jobId, step, note='') {
    await supabase.from('staff_day_status').upsert({
      staff_id: staffMember.id, date: selectedDate, job_id: jobId, step, note, updated_at: new Date().toISOString()
    }, { onConflict: 'staff_id,date' })
  }

  function sendSMS(message) {
    // Fire and forget — never blocks the flow if this fails or Twilio isn't set up yet
    fetch('/.netlify/functions/send-sms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message }),
    }).catch(() => {})
  }

  function getLocation(timeoutMs = 6000) {
    return new Promise(resolve => {
      if (!navigator.geolocation) return resolve(null)
      const timer = setTimeout(() => resolve(null), timeoutMs)
      navigator.geolocation.getCurrentPosition(
        pos => { clearTimeout(timer); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }) },
        () => { clearTimeout(timer); resolve(null) },
        { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
      )
    })
  }

  async function clockIn(jobId, clientId) {
    const now = new Date().toISOString()
    await supabase.from('time_logs').upsert({
      job_id: jobId, staff_id: staffMember.id, clock_in: now
    }, { onConflict: 'job_id,staff_id' })
    setTimeLogs(p => ({ ...p, [jobId]: { ...(p[jobId]||{}), clock_in: now } }))
    getLocation().then(loc => {
      if (loc) supabase.from('time_logs').update({ clock_in_lat: loc.lat, clock_in_lng: loc.lng }).eq('job_id', jobId).eq('staff_id', staffMember.id)
    })

    const clientNameForSms = clients.find(c=>c.id===clientId)?.nickname || clients.find(c=>c.id===clientId)?.name || 'un cliente'
    sendSMS(`🟢 ${staffMember.name} llegó a ${clientNameForSms} — ${new Date(now).toLocaleTimeString('es-MX',{hour:'numeric',minute:'2-digit'})}`)

    // Pull the last team's handoff note for this client, if any
    if (clientId && !priorHandoffNotes[jobId]) {
      const { data: lastSignoff } = await supabase.from('job_signoffs').select('comments')
        .eq('client_id', clientId).not('comments','is',null).order('signed_at',{ascending:false}).limit(1).maybeSingle()
      if (lastSignoff?.comments) setPriorHandoffNotes(p=>({...p,[jobId]:lastSignoff.comments}))
    }

    const job = jobs.find(j=>j.id===jobId)
    if (job?.staff_alert) {
      setGateSecs(5)
      setFlowStep('staff_alert')
      updateDayStatus(jobId, 'staff_alert')
      return
    }
    proceedAfterClockIn(jobId)
  }

  // After the walkthrough: houses we've been to get the "this is your time" screen;
  // new houses ask each person to confirm the time (or say they need more/less).
  function goToTimeStep(jobId) {
    const job = jobs.find(j=>j.id===jobId)
    setGateSecs(5)
    if (job && !beenBefore[job.client_id] && !jobEstimates[jobId]) {
      setEstimateInput(currentJobHours(jobId))
      setEstimateNote('')
      setFlowStep('estimate')
      updateDayStatus(jobId, 'estimate')
    } else {
      setFlowStep('time_info')
      updateDayStatus(jobId, 'time_info')
    }
  }

  function proceedAfterClockIn(jobId) {
    const job = jobs.find(j=>j.id===jobId)
    if (job?.skip_walkthrough || jobWalkthroughs[jobId]) {
      // Excluded manually, or already done by someone on the team today
      goToTimeStep(jobId)
    } else if (teamStaffIds.length > 1) {
      setFlowStep('assign_walkthrough')
      updateDayStatus(jobId, 'assign_walkthrough')
    } else {
      setWalkthroughSecs(5)
      setFlowStep('walkthrough')
      updateDayStatus(jobId, 'walkthrough')
    }
  }

  function chooseWalkthroughAssignee(jobId, choice) {
    setWalkthroughAssignee(choice)
    if (choice === staffMember.id || choice === 'todos') {
      setWalkthroughSecs(5)
      setFlowStep('walkthrough')
      updateDayStatus(jobId, 'walkthrough')
    } else {
      setFlowStep('waiting_walkthrough')
      updateDayStatus(jobId, 'waiting_walkthrough')
    }
  }

  async function checkWalkthroughDone(jobId) {
    setCheckingApproval(true)
    const { data } = await supabase.from('job_walkthroughs').select('*').eq('job_id', jobId).maybeSingle()
    setCheckingApproval(false)
    if (data) {
      setJobWalkthroughs(p=>({...p,[jobId]:data}))
      goToTimeStep(jobId)
    }
  }

  async function completeWalkthrough(jobId) {
    if (!jobWalkthroughs[jobId]) {
      const { data } = await supabase.from('job_walkthroughs')
        .upsert({ job_id: jobId, staff_id: staffMember.id }, { onConflict: 'job_id', ignoreDuplicates: true })
        .select().maybeSingle()
      if (data) setJobWalkthroughs(p=>({...p,[jobId]:data}))
    }
    goToTimeStep(jobId)
  }

  function currentJobHours(jobId) {
    return parseFloat(jobs.find(j=>j.id===jobId)?.hours) || 2
  }

  async function submitFirstTimeEstimate(jobId, scheduledHours, response) {
    setSubmittingEstimate(true)
    const sched = parseFloat(scheduledHours) || 0
    const hrs = response === 'ok' ? sched : estimateInput
    const row = {
      job_id: jobId, staff_id: staffMember.id,
      scheduled_hours: sched, estimated_hours: hrs, response,
      flag: response === 'more' ? 'over' : response === 'less' ? 'under' : 'ok',
      reason: response === 'ok' ? null : (estimateNote.trim() || null),
      approved: response === 'ok' ? true : null,   // "the time is fine" needs nothing from Yosi
    }
    const { data, error } = await supabase.from('job_estimates')
      .upsert(row, { onConflict: 'job_id,staff_id' })
      .select().maybeSingle()
    if (error) {
      setSubmittingEstimate(false)
      alert('No se pudo guardar. Revisa tu conexión e intenta otra vez.')
      return
    }
    if (data) setJobEstimates(p=>({...p,[jobId]:data}))
    const job = jobs.find(j=>j.id===jobId)
    setSubmittingEstimate(false)
    setGateSecs(5)
    if (job?.clean_type==='airbnb' && !photoConfirmations[jobId]?.before_sent) {
      setFlowStep('before_photos_confirm')
      updateDayStatus(jobId, 'before_photos_confirm')
    } else {
      setFlowStep('checklist_final')
      updateDayStatus(jobId, 'checklist_final')
    }
  }

  async function confirmPhotos(jobId, type) {
    setConfirmingPhotos(true)
    const field = type === 'before'
      ? { before_sent: true, before_confirmed_at: new Date().toISOString() }
      : { after_sent: true, after_confirmed_at: new Date().toISOString() }
    const { data } = await supabase.from('photo_confirmations')
      .upsert({ job_id: jobId, staff_id: staffMember.id, ...field }, { onConflict: 'job_id' })
      .select().maybeSingle()
    if (data) setPhotoConfirmations(p=>({...p,[jobId]:data}))
    setConfirmingPhotos(false)
    if (type === 'before') {
      setFlowStep('checklist_final')
      updateDayStatus(jobId, 'checklist_final')
    } else {
      setFlowStep(null)
    }
  }

  async function submitDamageReport(jobId, clientId) {
    setSubmittingDamage(true)
    let photoUrl = null
    if (damagePhoto) {
      try {
        const fileName = `damage-${jobId}-${Date.now()}.jpg`
        const { error: upErr } = await supabase.storage.from('job-photos').upload(fileName, damagePhoto)
        if (!upErr) {
          const { data: pub } = supabase.storage.from('job-photos').getPublicUrl(fileName)
          photoUrl = pub?.publicUrl || null
        }
      } catch(e) { /* storage bucket may not exist yet — continue without blocking */ }
    }
    await supabase.from('damage_reports').insert({
      job_id: jobId, client_id: clientId, staff_id: staffMember.id, photo_url: photoUrl, explanation: damageExplanation.trim()
    })
    setSubmittingDamage(false)
    setDamageSaved(true)
    setTimeout(()=>{ setDamageSaved(false); setShowDamageForm(false); setDamagePhoto(null); setDamageExplanation('') }, 2000)
  }

  async function submitQuickNote(jobId, clientId) {
    if (!quickNoteText.trim()) return
    await supabase.from('client_note_submissions').insert({
      job_id: jobId, client_id: clientId, staff_id: staffMember.id, note: quickNoteText.trim()
    })
    setQuickNoteText('')
    setQuickNoteSaved(true)
    setTimeout(()=>setQuickNoteSaved(false), 2500)
  }

  async function submitEta(jobId) {
    const note = etaChoice === 'break' ? `Tomando un descanso. ${etaText}`.trim()
      : etaChoice === 'early' ? `Puede empezar antes. ${etaText}`.trim()
      : etaChoice === 'ontime' ? `Llegará a tiempo. ${etaText}`.trim()
      : etaText
    await updateDayStatus(jobId, 'traveling', note)
    setEtaText('')
    setEtaChoice(null)
    setFlowStep(null)
  }

  async function clockOut(jobId) {
    const now = new Date().toISOString()
    await supabase.from('time_logs').update({ clock_out: now }).eq('job_id', jobId).eq('staff_id', staffMember.id)
    setTimeLogs(p => ({ ...p, [jobId]: { ...(p[jobId]||{}), clock_out: now } }))
    setJustClockedOut(jobId)
    updateDayStatus(jobId, 'clocked_out')
    getLocation().then(loc => {
      if (loc) supabase.from('time_logs').update({ clock_out_lat: loc.lat, clock_out_lng: loc.lng }).eq('job_id', jobId).eq('staff_id', staffMember.id)
    })
    const clientNameOut = clients.find(c=>c.id===jobs.find(j=>j.id===jobId)?.client_id)?.nickname || clients.find(c=>c.id===jobs.find(j=>j.id===jobId)?.client_id)?.name || 'un cliente'
    sendSMS(`🏁 ${staffMember.name} salió de ${clientNameOut} — ${new Date(now).toLocaleTimeString('es-MX',{hour:'numeric',minute:'2-digit'})}`)
    setTimeout(() => {
      setJustClockedOut(null)
      const remaining = jobs.filter(j => j.id !== jobId && !timeLogs[j.id]?.clock_out)
      if (remaining.length > 0) setFlowStep('eta')
    }, 1800)
  }

  function logout() {
    localStorage.removeItem('staff_portal_id')
    setStaffMember(null)
    setPin('')
  }

  // PIN LOGIN SCREEN
  if (showTraining) return <TrainingMode onExit={()=>setShowTraining(false)} />

  if (!staffMember) return (
    <div style={{ minHeight:'100vh', background:'#f8f9fa', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'system-ui,sans-serif' }}>
      <div style={{ background:'#fff', borderRadius:20, padding:'36px 32px', width:300, boxShadow:'0 8px 40px rgba(0,0,0,0.1)', textAlign:'center' }}>
        <div style={{ fontSize:40, marginBottom:8 }}>🐈</div>
        <div style={{ fontSize:20, fontWeight:700, color:'#e8547a', marginBottom:4 }}>Kitty's Cleaning</div>
        <div style={{ fontSize:13, color:'#80868b', marginBottom:28 }}>Ingresa tu PIN de 6 dígitos</div>
        <div style={{ display:'flex', justifyContent:'center', gap:10, marginBottom:20 }}>
          {[0,1,2,3,4,5].map(i => (
            <div key={i} style={{ width:44, height:52, border:`2px solid ${pin.length>i?'#e8547a':'#e3e5e8'}`, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, fontWeight:700, color:'#e8547a', background:pin.length>i?'#fdeef3':'#fff' }}>
              {pin.length>i?'●':''}
            </div>
          ))}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:12 }}>
          {['1','2','3','4','5','6','7','8','9','','0','⌫'].map((k,i) => (
            <button key={i} style={{ padding:'14px', borderRadius:12, border:'1px solid #e3e5e8', background:k?'#fff':'transparent', fontSize:18, fontWeight:600, cursor:k?'pointer':'default', color:'#202124' }}
              onClick={()=>{
                if (k==='⌫') setPin(p=>p.slice(0,-1))
                else if (k) setPin(p=>p.length<6?p+k:p)
              }}>
              {k}
            </button>
          ))}
        </div>
        {error && <div style={{ color:'#e24b4a', fontSize:12, marginBottom:10 }}>{error}</div>}
        <button style={{ width:'100%', padding:'13px', background:'#e8547a', color:'#fff', border:'none', borderRadius:12, fontSize:15, fontWeight:700, cursor:'pointer', marginBottom:10 }}
          onClick={login} disabled={loading}>
          {loading ? 'Verificando...' : 'Entrar'}
        </button>
        <button style={{ width:'100%', padding:'10px', background:'none', color:'#185fa5', border:'none', fontSize:12, fontWeight:600, cursor:'pointer' }}
          onClick={()=>setShowTraining(true)}>
          🎓 ¿Nueva? Practica aquí primero
        </button>
      </div>
    </div>
  )

  // MAIN PORTAL
  return (
    <div style={{ minHeight:'100vh', background:'#f8f9fa', fontFamily:'system-ui,sans-serif' }}>
      <style>{`
        @keyframes kittyPop {
          0% { transform: scale(0.6); opacity: 0; }
          60% { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      {/* Header */}
      <div style={{ background:'#e8547a', color:'#fff', padding:'16px', position:'sticky', top:0, zIndex:10, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:11, opacity:.8, textTransform:'uppercase', letterSpacing:'.06em' }}>Kitty's Cleaning 🐈</div>
          <div style={{ fontSize:18, fontWeight:700, marginTop:1 }}>Hola, {staffMember.name.split(' ')[0]}!</div>
        </div>
        <button style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.3)', color:'#fff', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:12 }}
          onClick={logout}>Salir</button>
      </div>

      <div style={{ padding:'12px 12px 60px' }}>


        {/* Announcements */}
        {announcements.length > 0 && (
          <div style={{ background:'#fdeef3', borderRadius:12, padding:'12px 16px', marginBottom:12, border:'1px solid #f4b8cc' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#c03060', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>🐾 Avisos de Yosi</div>
            {announcements.map(a => (
              <div key={a.id} style={{ padding:'8px 0', borderBottom:'1px solid #f4b8cc' }}>
                <div style={{ fontSize:13, color:'#5a1a2a', lineHeight:1.5, marginBottom:6 }}>{a.body}</div>
                <div style={{ display:'flex', gap:6 }}>
                  {['👍','✅','👀'].map(emoji => (
                    <button key={emoji} onClick={()=>reactToAnnouncement(a.id, emoji)}
                      style={{ fontSize:15, padding:'3px 9px', borderRadius:99, border:'none', cursor:'pointer',
                        background: myReactions[a.id]===emoji ? '#e8547a' : '#fff', opacity: myReactions[a.id]===emoji ? 1 : 0.6 }}>
                      {emoji}
                    </button>
                  ))}
                  {myReactions[a.id] && <span style={{ fontSize:11, color:'#1d9e75', fontWeight:600, alignSelf:'center' }}>✓ Visto</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {!equipmentCheckedOut ? (
          <div style={{ background:'#fff', borderRadius:20, padding:'20px 18px', border:'1px solid #fde3ec' }}>
            <div style={{ fontSize:11, fontWeight:800, color:'#e8547a', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>🧰 Antes de empezar</div>
            <div style={{ fontSize:16, fontWeight:800, color:'#3a2530', marginBottom:14 }}>¿Qué equipo te llevaste hoy?</div>
            {Array.from(new Set(equipmentItems.map(it=>it.category||'General'))).map(cat => (
              <div key={cat} style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#80868b', marginBottom:6 }}>{cat}</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {equipmentItems.filter(it=>(it.category||'General')===cat).map(it => {
                    const picked = selectedEquipment.includes(it.id)
                    return (
                      <button key={it.id} onClick={()=>toggleEquipment(it.id)}
                        style={{ padding:'9px 14px', borderRadius:10, border:'none', cursor:'pointer', fontSize:13, fontWeight:700,
                          background: picked ? '#e8547a' : '#f1f3f4', color: picked ? '#fff' : '#5f6368' }}>
                        {picked?'✓ ':''}{it.name}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
            <textarea value={equipmentIssue} onChange={e=>setEquipmentIssue(e.target.value)}
              placeholder="¿Algo falta o está dañado? (opcional)"
              style={{ width:'100%', padding:10, border:'1px solid #e3e5e8', borderRadius:10, fontSize:13, resize:'none', height:56, fontFamily:'inherit', marginBottom:14 }} />
            <button onClick={submitEquipmentCheckout} disabled={submittingEquipment||selectedEquipment.length===0}
              style={{ width:'100%', padding:16, borderRadius:14, border:'none', background:'#1d9e75', color:'#fff', fontWeight:800, fontSize:15, cursor:'pointer' }}>
              {submittingEquipment?'...':`✓ Listo, continuar${selectedEquipment.length?` (${selectedEquipment.length})`:''}`}
            </button>
          </div>
        ) : showWelcome ? (
          <WelcomeScreen
            staffName={staffMember.name.split(' ')[0]}
            teammateNames={teamStaffIds.filter(id=>id!==staffMember.id).map(id=>allStaff.find(s=>s.id===id)?.name?.split(' ')[0]).filter(Boolean)}
            houseCount={jobs.length}
            totalHours={jobs.reduce((sum,j)=>sum+(parseFloat(j.hours)||0),0)}
            onStart={()=>setShowWelcome(false)}
          />
        ) : (
        <>
        {/* Header bar — just the full-schedule icon now, no more tabs needed */}
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
          <button onClick={()=>setShowFullSchedule(true)} title="Ver mi horario completo de hoy"
            style={{ width:44, height:44, flexShrink:0, borderRadius:12, border:'none', background:'#fff', boxShadow:'0 1px 6px rgba(0,0,0,0.05)', fontSize:18, cursor:'pointer' }}>
            📋
          </button>
        </div>

        {showFullSchedule && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'flex-end', zIndex:200 }} onClick={()=>setShowFullSchedule(false)}>
            <div style={{ background:'#fff', width:'100%', borderRadius:'20px 20px 0 0', padding:'20px 20px 32px', maxHeight:'80vh', overflowY:'auto' }} onClick={e=>e.stopPropagation()}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                <div style={{ fontSize:15, fontWeight:800, color:'#3a2530' }}>📋 Tu horario de hoy</div>
                <button onClick={()=>setShowFullSchedule(false)} style={{ border:'none', background:'none', fontSize:22, color:'#9aa0a6', cursor:'pointer' }}>×</button>
              </div>
              <div style={{ fontSize:11, color:'#c05050', fontWeight:600, marginBottom:14, background:'#fff3f3', padding:'8px 10px', borderRadius:8 }}>
                ⚠️ Esto es tal como está ahora mismo — puede cambiar durante el día.
              </div>
              {jobs.map((job,i) => {
                const cl = clients.find(c=>c.id===job.client_id)
                const done = !!timeLogs[job.id]?.clock_out
                return (
                  <div key={job.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom: i<jobs.length-1?'1px solid #e3e5e8':'none' }}>
                    <div style={{ width:26, height:26, borderRadius:'50%', background: done?'#1d9e75':'#e3e5e8', color: done?'#fff':'#80868b', fontSize:12, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      {done?'✓':i+1}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'#202124' }}>{cl?.nickname||cl?.name||'Cliente'}</div>
                      <div style={{ fontSize:11, color:'#80868b' }}>{job.start_time||'Todo el día'}{cl?.address?' · '+cl.address:''}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign:'center', padding:40, color:'#9aa0a6' }}>Cargando...</div>
        ) : jobs.length===0 ? (
          <div style={{ textAlign:'center', padding:48, color:'#9aa0a6' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
            <div style={{ fontSize:16, fontWeight:500 }}>Sin trabajos asignados</div>
            <div style={{ fontSize:13, marginTop:6 }}>para {new Date(selectedDate+'T12:00:00').toLocaleDateString('es-MX',{weekday:'long',month:'long',day:'numeric'})}</div>
          </div>
        ) : (()=>{
          const doneCount = jobs.filter(j => timeLogs[j.id]?.clock_out).length
          const totalCount = jobs.length
          const pct = totalCount ? Math.round(doneCount/totalCount*100) : 0
          const currentJob = jobs.find(j => !timeLogs[j.id]?.clock_out)
          const currentClient = currentJob ? clients.find(c=>c.id===currentJob.client_id) : null
          const currentLog = currentJob ? timeLogs[currentJob.id] : null
          const clockedIn = !!currentLog?.clock_in
          const isSignedCurrent = currentJob ? !!signoffs[currentJob.id] : false
          const cleanType = currentJob?.clean_type || 'regular'
          const baseChecklist = cleanType==='custom' ? (currentJob?.custom_checklist||[]) : (templates[cleanType] || DEFAULT_CHECKLISTS.regular)
          const checkItems = [...baseChecklist, ...(currentJob?.one_time_items||[])]
          const checked = currentJob ? (checklist[currentJob.id] || []) : []
          const allDone = checkItems.every(item=>checked.includes(item))

          return (
            <div style={{ background:'linear-gradient(180deg,#fff 0%,#fff8fb 100%)', borderRadius:20, padding:'20px 18px', marginBottom:16, boxShadow:'0 4px 16px rgba(232,84,122,0.12)', border:'1px solid #fde3ec' }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, fontWeight:700, color:'#c98aa3', marginBottom:6 }}>
                <span>🐾 Progreso de hoy</span>
                <span>{doneCount}/{totalCount} casas · {pct}%</span>
              </div>
              <div style={{ height:10, background:'#fde3ec', borderRadius:99, overflow:'hidden', marginBottom:20 }}>
                <div style={{ height:'100%', width:`${pct}%`, background:'linear-gradient(90deg,#ffb3c6,#e8547a)', borderRadius:99, transition:'width .5s ease' }} />
              </div>

              {justClockedOut ? (
                <div style={{ textAlign:'center', padding:'20px 0', animation:'kittyPop .4s ease' }}>
                  <div style={{ fontSize:52, marginBottom:8 }}>✅🐈</div>
                  <div style={{ fontSize:20, fontWeight:800, color:'#1d9e75' }}>¡Muy bien! 🎉</div>
                </div>

              ) : flowStep === 'eta' && currentJob ? (
                <EtaStep nextClient={currentClient?.nickname||currentClient?.name||'tu próxima casa'} nextTime={currentJob.start_time||'la hora programada'}
                  nextAddress={currentClient?.address ? currentClient.address+(currentClient.city?', '+currentClient.city:'') : ''}
                  etaText={etaText} setEtaText={setEtaText} etaChoice={etaChoice} setEtaChoice={setEtaChoice}
                  onSubmit={()=>submitEta(currentJob.id)} />

              ) : !currentJob ? (
                <div style={{ textAlign:'center', padding:'20px 0' }}>
                  <div style={{ fontSize:48, marginBottom:8 }}>🎉🐾</div>
                  <div style={{ fontSize:18, fontWeight:800, color:'#1d9e75' }}>¡Terminaste todas tus casas de hoy!</div>
                </div>

              ) : !clockedIn ? (
                <div>
                  <div style={{ fontSize:11, fontWeight:800, color:'#e8547a', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>
                    🏠 Casa actual
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2 }}>
                    <div style={{ fontSize:23, fontWeight:800, color:'#3a2530' }}>{currentClient?.nickname||currentClient?.name||'Cliente'}</div>
                    {!currentJob.skip_walkthrough && !jobWalkthroughs[currentJob.id] && (
                      <span style={{ fontSize:10, fontWeight:800, color:'#185fa5', background:'#eaf2fb', border:'1px solid #c5d9f5', borderRadius:99, padding:'3px 9px', letterSpacing:'.04em' }}>
                        PRIMERA VEZ
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize:13, color:'#a98a97', marginBottom:18 }}>
                    {currentJob.start_time||'Todo el día'}{currentClient?.address?` · ${currentClient.address}`:''}
                  </div>
                  <button onClick={()=>setConfirmAction({type:'in', jobId:currentJob.id, clientId:currentJob.client_id, clientName:currentClient?.nickname||currentClient?.name||'este cliente'})}
                    style={{ width:'100%', padding:17, borderRadius:16, border:'none', background:'linear-gradient(135deg,#2ec090,#1d9e75)', color:'#fff', fontWeight:800, fontSize:16, cursor:'pointer', boxShadow:'0 4px 12px rgba(29,158,117,0.3)' }}>
                    🟢 Clock In
                  </button>
                </div>

              ) : !isSignedCurrent && flowStep === 'staff_alert' ? (
                <StaffAlertStep message={currentJob.staff_alert} secs={gateSecs} onTick={setGateSecs} onContinue={()=>proceedAfterClockIn(currentJob.id)} />

              ) : !isSignedCurrent && flowStep === 'assign_walkthrough' ? (
                <AssignWalkthroughStep team={teamStaffIds.map(id=>allStaff.find(s=>s.id===id)).filter(Boolean)} myId={staffMember.id}
                  onChoose={(choice)=>chooseWalkthroughAssignee(currentJob.id, choice)} />

              ) : !isSignedCurrent && flowStep === 'waiting_walkthrough' ? (
                <WaitingWalkthroughStep
                  assigneeName={walkthroughAssignee ? (allStaff.find(s=>s.id===walkthroughAssignee)?.name || 'tu compañera') : 'tu compañera'}
                  checking={checkingApproval} onCheck={()=>checkWalkthroughDone(currentJob.id)} />

              ) : !isSignedCurrent && flowStep === 'walkthrough' ? (
                <WalkthroughStep seconds={walkthroughSecs} onTick={setWalkthroughSecs} onDone={()=>completeWalkthrough(currentJob.id)}
                  exempt={!!jobWalkthroughs[currentJob.id] || !!currentJob.skip_walkthrough} client={currentClient} job={currentJob} checkItems={checkItems}
                  cleanTypeLabel={TYPE_LABELS[cleanType]||cleanType} priorNote={priorHandoffNotes[currentJob.id]}
                  quickNoteText={quickNoteText} setQuickNoteText={setQuickNoteText} quickNoteSaved={quickNoteSaved}
                  onSaveNote={()=>submitQuickNote(currentJob.id, currentJob.client_id)}
                  checkedClientNotes={checklist['clientnotes-'+currentJob.id] || []}
                  onToggleClientNote={(note)=>toggleCheck('clientnotes-'+currentJob.id, note)} />

              ) : !isSignedCurrent && flowStep === 'time_info' ? (
                <TimeInfoStep hours={currentJob.hours} specialNote={currentJob.special_time_note} newHouse={!beenBefore[currentJob.client_id]} secs={gateSecs} onTick={setGateSecs}
                  onContinue={()=>{
                    setGateSecs(5)
                    if (cleanType!=='airbnb' || photoConfirmations[currentJob.id]?.before_sent) { setFlowStep('checklist_final'); updateDayStatus(currentJob.id,'checklist_final') }
                    else { setFlowStep('before_photos_confirm'); updateDayStatus(currentJob.id,'before_photos_confirm') }
                  }} />

              ) : !isSignedCurrent && flowStep === 'estimate' ? (
                <EstimateStep hours={estimateInput} setHours={setEstimateInput} scheduledHours={parseFloat(currentJob.hours)||2}
                  note={estimateNote} setNote={setEstimateNote}
                  submitting={submittingEstimate} secs={gateSecs} onTick={setGateSecs}
                  onSubmit={(response)=>submitFirstTimeEstimate(currentJob.id, currentJob.hours, response)} />

              ) : !isSignedCurrent && flowStep === 'before_photos_confirm' ? (
                <PhotoConfirmStep type="before" cleanType={cleanType} confirming={confirmingPhotos} secs={gateSecs} onTick={setGateSecs} onConfirm={()=>confirmPhotos(currentJob.id,'before')}
                  onGoBack={()=>setFlowStep('walkthrough')} />

              ) : !isSignedCurrent ? (
                <ChecklistFinalStep checkItems={checkItems} checked={checked} onToggle={item=>toggleCheck(currentJob.id,item)}
                  allDone={allDone} comments={comments[currentJob.id]||''} onCommentChange={v=>setComments(p=>({...p,[currentJob.id]:v}))}
                  onSignOff={()=>setShowSign(currentJob.id)} cleanType={cleanType} onGoBack={()=>setFlowStep('walkthrough')}
                  showDamageForm={showDamageForm} setShowDamageForm={setShowDamageForm}
                  damagePhoto={damagePhoto} setDamagePhoto={setDamagePhoto}
                  damageExplanation={damageExplanation} setDamageExplanation={setDamageExplanation}
                  submittingDamage={submittingDamage} damageSaved={damageSaved}
                  onSubmitDamage={()=>submitDamageReport(currentJob.id, currentJob.client_id)}
                  clientNotesList={currentClient?.notes_list}
                  checkedClientNotes={checklist['clientnotes-'+currentJob.id] || []}
                  onToggleClientNote={(note)=>toggleCheck('clientnotes-'+currentJob.id, note)}
                  missingSupplies={missingSupplies} setMissingSupplies={setMissingSupplies} />

              ) : (cleanType==='airbnb' && !photoConfirmations[currentJob.id]?.after_sent) ? (
                <PhotoConfirmStep type="after" cleanType={cleanType} confirming={confirmingPhotos} secs={gateSecs} onTick={setGateSecs} onConfirm={()=>confirmPhotos(currentJob.id,'after')} />

              ) : (
                <div>
                  <div style={{ fontSize:12, color:'#1d9e75', fontWeight:700, marginBottom:12, textAlign:'center' }}>
                    ✓ Checklist firmado · {signoffs[currentJob.id]?.initials}
                  </div>
                  <button onClick={()=>setConfirmAction({type:'out', jobId:currentJob.id, clientName:currentClient?.nickname||currentClient?.name||'este cliente'})}
                    style={{ width:'100%', padding:17, borderRadius:16, border:'none', background:'linear-gradient(135deg,#f0678f,#e8547a)', color:'#fff', fontWeight:800, fontSize:16, cursor:'pointer', boxShadow:'0 4px 12px rgba(232,84,122,0.3)' }}>
                    🔴 Clock Out
                  </button>
                </div>
              )}

              {clockedIn && currentJob && !justClockedOut && flowStep !== 'eta' && (
                <div style={{ marginTop:16, paddingTop:12, borderTop:'1px solid #fde3ec', textAlign:'center' }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#c98aa3' }}>📍 {currentClient?.nickname||currentClient?.name||'Cliente'}</div>
                  {currentClient?.address && (
                    <div style={{ fontSize:11, color:'#9aa0a6', marginTop:2 }}>{currentClient.address}{currentClient.city?', '+currentClient.city:''}</div>
                  )}
                  <div style={{ fontSize:11, fontWeight:800, color:'#e8547a', marginTop:4 }}>{TYPE_LABELS[cleanType]||cleanType}</div>
                </div>
              )}
            </div>
          )
        })()}
        </>
        )}
      </div>

      {/* Sign-off modal */}
      {showSign && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'flex-end', zIndex:100 }}>
          <div style={{ background:'#fff', width:'100%', borderRadius:'20px 20px 0 0', padding:'24px 20px 40px' }}>
            <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>Sign Off 🐈</div>
            <div style={{ fontSize:13, color:'#5f6368', marginBottom:16 }}>Confirma que completaste todas las tareas.</div>
            <div style={{ fontSize:24, fontWeight:700, color:'#e8547a', fontFamily:'Georgia,serif', textAlign:'center', letterSpacing:8, padding:'16px', border:'2px solid #e8547a', borderRadius:12, marginBottom:16 }}>
              {initials || '—'}
            </div>
            <div style={{ fontSize:12, color:'#80868b', textAlign:'center', marginBottom:16 }}>Tus iniciales: {initials}</div>
            <button style={{ width:'100%', padding:'14px', background:'#e8547a', color:'#fff', fontWeight:700, fontSize:16, border:'none', borderRadius:12, cursor:'pointer', marginBottom:8 }}
              onClick={()=>signOff(showSign)} disabled={saving[showSign]}>
              {saving[showSign]?'Guardando...':'✓ Confirmar y guardar'}
            </button>
            <button style={{ width:'100%', padding:'12px', background:'none', color:'#80868b', border:'none', fontSize:14 }}
              onClick={()=>setShowSign(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Clock in/out confirm modal */}
      {confirmAction && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'flex-end', zIndex:110 }}>
          <div style={{ background:'#fff', width:'100%', borderRadius:'20px 20px 0 0', padding:'26px 20px 40px', textAlign:'center' }}>
            <div style={{ fontSize:44, marginBottom:8 }}>{confirmAction.type==='in'?'🟢🐾':'🔴🐾'}</div>
            <div style={{ fontSize:18, fontWeight:800, marginBottom:6 }}>
              {confirmAction.type==='in' ? `¿Ya llegaste a ${confirmAction.clientName}?` : `¿Ya terminaste en ${confirmAction.clientName}?`}
            </div>
            <div style={{ fontSize:13, color:'#80868b', marginBottom:20 }}>
              {confirmAction.type==='in' ? 'Vamos a marcar tu hora de llegada.' : 'Vamos a marcar tu hora de salida.'}
            </div>
            <button style={{ width:'100%', padding:'15px', background:confirmAction.type==='in'?'#1d9e75':'#e8547a', color:'#fff', fontWeight:800, fontSize:16, border:'none', borderRadius:14, cursor:'pointer', marginBottom:8 }}
              onClick={()=>{
                if (confirmAction.type==='in') clockIn(confirmAction.jobId, confirmAction.clientId)
                else clockOut(confirmAction.jobId)
                setConfirmAction(null)
              }}>
              ✓ Sí, estoy segura
            </button>
            <button style={{ width:'100%', padding:'12px', background:'none', color:'#80868b', border:'none', fontSize:14 }}
              onClick={()=>setConfirmAction(null)}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  )
}
